// Lead-temperature reference accordion (US-161).
//
// Static read-only educational card the prototype renders at
// `prototypes_and_docs/ls-relance.jsx:255-391`. Three rows (very hot / warm /
// cold) — clicking a row toggles its expanded body; only one row open at a
// time, mirroring the proto behaviour. Tone colours use `var(--success)` /
// `var(--warning)` / `var(--info)` from the DS token surface so light + dark
// look right.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import { FOCUS_RING_CLASS } from '@barel/ui';

interface TempRow {
  id: 'hot' | 'warm' | 'cold';
  icon: string;
  label: string;
  short: string;
  signals: string[];
  action: string;
  /** Map to a CSS color token defined by the DS theme. */
  toneVar: string;
  bgVar: string;
  borderVar: string;
}

export function LeadTempInfo() {
  const t = useT('voice');
  const [openId, setOpenId] = useState<TempRow['id'] | null>(null);

  const rows: TempRow[] = [
    {
      id: 'hot',
      icon: '🔥',
      label: t('leadTemp.hotLabel'),
      short: t('leadTemp.hotShort'),
      signals: [
        t('leadTemp.hotSignal1'),
        t('leadTemp.hotSignal2'),
        t('leadTemp.hotSignal3'),
        t('leadTemp.hotSignal4'),
      ],
      action: t('leadTemp.hotAction'),
      toneVar: 'var(--error)',
      bgVar: 'var(--error-bg)',
      borderVar: 'var(--error-border)',
    },
    {
      id: 'warm',
      icon: '☀️',
      label: t('leadTemp.warmLabel'),
      short: t('leadTemp.warmShort'),
      signals: [
        t('leadTemp.warmSignal1'),
        t('leadTemp.warmSignal2'),
        t('leadTemp.warmSignal3'),
        t('leadTemp.warmSignal4'),
      ],
      action: t('leadTemp.warmAction'),
      toneVar: 'var(--warning)',
      bgVar: 'var(--warning-bg)',
      borderVar: 'var(--warning-border)',
    },
    {
      id: 'cold',
      icon: '❄️',
      label: t('leadTemp.coldLabel'),
      short: t('leadTemp.coldShort'),
      signals: [
        t('leadTemp.coldSignal1'),
        t('leadTemp.coldSignal2'),
        t('leadTemp.coldSignal3'),
        t('leadTemp.coldSignal4'),
      ],
      action: t('leadTemp.coldAction'),
      toneVar: 'var(--info)',
      bgVar: 'var(--info-bg)',
      borderVar: 'var(--info-border)',
    },
  ];

  return (
    <div
      data-component="LeadTempInfo"
      className="rounded-xl border border-default bg-bg-surface p-4"
    >
      <div className="mb-3 flex items-start gap-2.5">
        <span aria-hidden className="text-base leading-none">
          🌡️
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-primary">{t('leadTemp.header')}</div>
          <p className="mt-0.5 text-xs text-secondary">{t('leadTemp.intro')}</p>
        </div>
      </div>

      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const open = openId === row.id;
          return (
            <li
              key={row.id}
              className="overflow-hidden rounded-lg transition-colors duration-fast ease-out"
              style={{
                background: open ? row.bgVar : 'var(--bg-elevated)',
                border: `1px solid ${open ? row.borderVar : 'var(--border-default)'}`,
              }}
              data-testid={`lead-temp-${row.id}`}
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? null : row.id)}
                className={
                  'flex w-full items-center gap-3 px-3.5 py-2.5 text-left ' + FOCUS_RING_CLASS
                }
                aria-expanded={open}
                aria-controls={`lead-temp-body-${row.id}`}
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm"
                  style={{
                    background: row.bgVar,
                    border: `1px solid ${row.borderVar}`,
                  }}
                  aria-hidden
                >
                  {row.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold" style={{ color: row.toneVar }}>
                    {row.label}
                  </div>
                  <div className="text-xs text-tertiary">{row.short}</div>
                </div>
                <span aria-hidden className="text-xs text-tertiary">
                  {open ? '▴' : '▾'}
                </span>
              </button>
              {open ? (
                <div id={`lead-temp-body-${row.id}`} className="flex flex-col gap-2 px-3.5 pb-3">
                  <div>
                    <div className="text-2xs font-semibold uppercase tracking-wider text-secondary">
                      {t('leadTemp.signalsLabel')}
                    </div>
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {row.signals.map((s) => (
                        <li key={s} className="flex gap-1.5 text-xs text-primary">
                          <span aria-hidden style={{ color: row.toneVar }}>
                            •
                          </span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-md border border-subtle bg-bg-base px-2.5 py-2 text-xs text-secondary">
                    <span aria-hidden style={{ color: row.toneVar, fontWeight: 600 }}>
                      →{' '}
                    </span>
                    {row.action}
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
